import { z } from "zod";

// NOTE: NSAI-3304 — the one contract web, mobile and the browser extension all read
// for a sandboxed skill-script run. Every display value is server-decided: a client
// never derives a state, a limit or an error message of its own, and never receives
// a storage key, a signed URL or the script's source.

export enum ESkillScriptRunState {
  CANCELLED = "cancelled",
  FAILED = "failed",
  QUEUED = "queued",
  RUNNING = "running",
  SUCCEEDED = "succeeded",
  TIMED_OUT = "timed_out",
}

// NOTE: The full set of run states a client may see as final.
export const TERMINAL_SKILL_SCRIPT_RUN_STATES: readonly ESkillScriptRunState[] =
  [
    ESkillScriptRunState.SUCCEEDED,
    ESkillScriptRunState.FAILED,
    ESkillScriptRunState.TIMED_OUT,
    ESkillScriptRunState.CANCELLED,
  ] as const;

// NOTE: Safe, bounded error vocabulary. A sandbox failure is never reported to a
// client as a raw message: the codes below are the whole surface, so a script's
// stderr or an internal exception can never become the rendered explanation.
export enum ESkillScriptRunErrorCode {
  ARTIFACTS_REJECTED = "artifacts_rejected",
  CANCELLED = "cancelled",
  INTERNAL = "internal",
  LIMIT_EXCEEDED = "limit_exceeded",
  NOT_AUTHORIZED = "not_authorized",
  SANDBOX_UNAVAILABLE = "sandbox_unavailable",
  SCRIPT_FAILED = "script_failed",
  TIMED_OUT = "timed_out",
}

// NOTE: An artifact is referenced by its message-file uuid only. Downloads go
// through the authorized short-lived download route, never a storage key.
export const skillScriptArtifactSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
});
export type TSkillScriptArtifact = z.infer<typeof skillScriptArtifactSchema>;

// NOTE: The applied limits travel with the record so a client can explain a
// timeout or a truncation without hardcoding numbers that only the server knows.
export const skillScriptRunLimitsSchema = z.object({
  timeoutMs: z.number(),
  memoryBytes: z.number(),
  cpuCount: z.number(),
  maxArtifacts: z.number(),
  maxTotalArtifactBytes: z.number(),
  maxLogBytes: z.number(),
});
export type TSkillScriptRunLimits = z.infer<typeof skillScriptRunLimitsSchema>;

// NOTE: NSAI-6662 — a chat attachment the script was given, read-only, at
// `path` inside the sandbox. Name and path only: the file itself is already in
// the conversation.
export const skillScriptInputFileSchema = z.object({
  name: z.string(),
  path: z.string(),
});
export type TSkillScriptInputFile = z.infer<typeof skillScriptInputFileSchema>;

export const skillScriptRunRecordSchema = z.object({
  uuid: z.string(),
  skillUuid: z.string(),
  skillVersionUuid: z.string(),
  skillName: z.string(),
  scriptPath: z.string(),
  state: z.nativeEnum(ESkillScriptRunState),
  errorCode: z.nativeEnum(ESkillScriptRunErrorCode).nullable(),
  exitCode: z.number().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  // NOTE: Already truncated server-side to the log cap; `isOutputTruncated` says
  // so explicitly rather than leaving a client to guess from the length.
  stdout: z.string(),
  stderr: z.string(),
  isOutputTruncated: z.boolean(),
  artifacts: z.array(skillScriptArtifactSchema),
  // NOTE: NSAI-6662 — optional because a server from before it omits the field;
  // a client reads absent as "no input files".
  inputFiles: z.array(skillScriptInputFileSchema).optional(),
  limits: skillScriptRunLimitsSchema,
  position: z.number(),
});
export type TSkillScriptRunRecord = z.infer<typeof skillScriptRunRecordSchema>;

// NOTE: The live progress event. It carries the same record the authorized history
// read returns for that assistant message, so a refresh renders what the stream
// rendered; clients merge by run `uuid` rather than appending.
export const skillScriptRunEventSchema = z.object({
  chatUuid: z.string(),
  messageUuid: z.string(),
  run: skillScriptRunRecordSchema,
});
export type TSkillScriptRunEvent = z.infer<typeof skillScriptRunEventSchema>;

export const skillScriptArtifactDownloadSchema = z.object({
  downloadUrl: z.string(),
  expiresInSeconds: z.number(),
  fileName: z.string(),
  mimeType: z.string(),
});
export type TSkillScriptArtifactDownload = z.infer<
  typeof skillScriptArtifactDownloadSchema
>;

export const isTerminalSkillScriptRunState = (
  state: ESkillScriptRunState,
): boolean => TERMINAL_SKILL_SCRIPT_RUN_STATES.includes(state);
